// Fleet Reports — daily multi-series line chart (2026-07-14).
// Same rationale as FleetBar: local root, [data] deps, disposed in-effect.
//
// DAY-KEY RENDERING (a live bug in the existing TSChart, not forward-compat):
// this uses a CATEGORY axis keyed on the opaque 'YYYY-MM-DD' string, NOT a
// DateAxis. TSChart does `new Date(reading.date).getTime()` with amCharts'
// default root.utc=false, so a date-only key renders in the VIEWER's zone — for
// a UTC-4 viewer every LLA day label shows one day early ('2026-07-01' renders
// as 2026-06-30 20:00). A category axis is correct for any viewer anywhere.
import React, { useLayoutEffect, useRef } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";

// days:   ['2026-06-29', ...]                          (opaque category keys)
// series: [{ key, label, color, points: { '2026-06-29': 12.3, ... } }]
//
// `key` (the sensorId) is what the data field is keyed on — NOT `label`. Two
// vehicles may legitimately share a display name (nothing enforces uniqueness),
// and keying valueYField by the name would silently collapse them into one line.
// `label` is used only for the legend and the tooltip.
export default function FleetLine({ days, series, unit, height = "300px", valueFormat = "#.00" }) {
  const ref = useRef(null);

  useLayoutEffect(() => {
    if (!ref.current) return undefined;
    const root = am5.Root.new(ref.current);
    root.setThemes([am5themes_Animated.new(root)]);
    root._logo && root._logo.dispose();

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, { panX: false, panY: false, wheelX: "none", wheelY: "none", layout: root.verticalLayout })
    );

    const xRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 30 });
    xRenderer.labels.template.setAll({ fontSize: 11 });
    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, { categoryField: "date", renderer: xRenderer })
    );
    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, { renderer: am5xy.AxisRendererY.new(root, {}), min: 0 })
    );
    if (unit) {
      yAxis.children.unshift(
        am5.Label.new(root, { rotation: -90, text: unit, y: am5.p50, centerX: am5.p50, fontSize: 12, fill: am5.color("#666666") })
      );
    }

    // One row per day; each series contributes a column. A day a vehicle has no
    // data for is simply ABSENT from the row (undefined) — amCharts leaves a gap
    // rather than plotting 0. Rendering an absence as zero would be a lie.
    const rows = (days || []).map((d) => {
      const row = { date: d };
      for (const s of series || []) {
        const v = s.points[d];
        if (v !== null && v !== undefined) row[s.key] = v; // keyed by sensorId
      }
      return row;
    });
    xAxis.data.setAll(rows);

    for (const s of series || []) {
      const line = chart.series.push(
        am5xy.LineSeries.new(root, {
          name: s.label,
          xAxis,
          yAxis,
          valueYField: s.key, // sensorId — unique; the label may not be
          categoryXField: "date",
          connect: false, // do not bridge a missing day
          stroke: am5.color(s.color),
          fill: am5.color(s.color),
          tooltip: am5.Tooltip.new(root, {
            labelText: `{name}  {categoryX}: {valueY.formatNumber('${valueFormat}')} ${unit || ""}`,
          }),
        })
      );
      line.strokes.template.setAll({ strokeWidth: 2 });
      line.bullets.push(() =>
        am5.Bullet.new(root, {
          sprite: am5.Circle.new(root, { radius: 3, fill: am5.color(s.color) }),
        })
      );
      line.data.setAll(rows);
      line.appear(600);
    }

    const legend = chart.children.push(
      am5.Legend.new(root, { centerX: am5.p50, x: am5.p50, marginTop: 10 })
    );
    legend.labels.template.setAll({ fontSize: 12 });
    legend.data.setAll(chart.series.values);

    chart.set("cursor", am5xy.XYCursor.new(root, { behavior: "none" }));

    return () => root.dispose();
  }, [days, series, unit, valueFormat]);

  return <div ref={ref} style={{ width: "100%", height }} />;
}
